import { memo, useState, useEffect, useCallback, useMemo } from "react";
import Form from "../../components/Form";
import {
    getOwners,
    getRelationships,
    getAnimalsByGenderAndStatus,
} from "../../api";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../../components/styles/Select";
import { Box, Typography, Grid2, Button } from "@mui/material";
import Spinner from "../../components/Spinner.js";
import {
    femaleAnimalHeader,
    relationshipHeader,
} from "../../components/TableHeaders.js"
import useListStyles from "../../components/styles/List.js";
import Table from "../../components/Table.js";
import MatingsRelationshipsPdf from "./MatingsRelationshipsPdf.js";
import { BackButton } from "../../components/Buttons.js";

const MatingsRelationship = () => {
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [selectedMaleOwner, setSelectedMaleOwner] = useState(null);
    const [selectedFemaleOwner, setSelectedFemaleOwner] = useState(null);
    const [selectedMaleAnimal, setSelectedMaleAnimal] = useState(null);
    const [selectedNameMaleOwner, setSelectedNameMaleOwner] = useState(null);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [malesDropdownValues, setMalesDropdownValues] = useState([]);
    const [step, setStep] = useState(0);
    const [isLoading, setIsLoading] = useState(false);
    const [matingsRelationships, setMatingsRelationships] = useState([]);

    const classes = useSelectStyles();
    const listClasses = useListStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners)
                setOwnersDropdownValues(owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                })));

            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    );

    useEffect(() => {
        fetchData();
    }, []);

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Εκτροφή",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο"
        },
    ]

    const malesFormContent = [
        {
            customType: "dropdown",
            id: "maleAnimal",
            items: malesDropdownValues,
            placeholder: "Κριός",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επόμενο"
        },
    ]

    useEffect(() => {
        fetchData();
    }, []);

    const tableColumns = useMemo(() => [
        femaleAnimalHeader,
        relationshipHeader,
    ]);

    const handleMaleOwnerSelect = async (values) => {
        setIsLoading(true);
        const ownerId = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)?._id;
        setSelectedMaleOwner(ownerId);
        setSelectedNameMaleOwner(values.owner);
        const { success: scs, animals } = await getAnimalsByGenderAndStatus(ownerId, "male", "alive");
        if (scs) {
            if (animals.length > 0) {
                setMalesDropdownValues(animals.map((animal) => ({
                    value: `${animal.registrationCode}`,
                    text: `${animal.registrationCode}`
                })));
                setStep(1);
            } else {
                error("Δεν υπάρχουν κριοί για τη συγκεκριμένη εκτροφή")
            }
        } else {
            error("Κάτι πήγε στραβά");
        }
        setIsLoading(false);
    };

    const handleMaleAnimalSelect = (values) => {
        setSelectedMaleAnimal(values.maleAnimal);
        setStep(2);
    }

    const handleFemaleOwnerSelect = async (values) => {
        setIsLoading(true);
        const femaleOwnerId = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)?._id;
        setSelectedFemaleOwner(values.owner);
        console.log("The values to send for the check", selectedMaleOwner, selectedMaleAnimal, femaleOwnerId);
        const { success: scs, relationships, message } = await getRelationships(selectedMaleAnimal, selectedMaleOwner, femaleOwnerId)
        if (scs) {
            setMatingsRelationships(relationships);
            setStep(3);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    return (
        <>
            <Spinner open={isLoading} />
            {step !== 3 && step !== 4 && (
                <Box className={classes.outerBox}>
                    <Box container flexDirection="column" className={classes.innerBox}>
                        {step === 0 && (
                            <>
                                <Typography variant="h5" sx={{ margin: "1rem" }}>
                                    Επιλέξτε την Εκτροφή του κριού:
                                </Typography>
                                <Form content={ownerFormContent} onSubmit={handleMaleOwnerSelect} />
                            </>
                        )}
                        {step === 1 && (
                            <>
                                <Typography variant="h5" sx={{ margin: "1rem" }}>
                                    Επιλέξτε τον κριό που σας ενδιαφέρει:
                                </Typography>
                                <Form content={malesFormContent} onSubmit={handleMaleAnimalSelect} />
                            </>
                        )}
                        {step === 2 && (
                            <>
                                <Typography variant="h5" sx={{ margin: "1rem" }}>
                                    Επιλέξτε την Εκτροφή για τις προβατίνες:
                                </Typography>
                                <Form content={ownerFormContent} onSubmit={handleFemaleOwnerSelect} />
                            </>
                        )}
                        {step !== 0 && (
                            <BackButton onClick={() => setStep(step - 1)} />
                        )}
                    </Box>
                </Box>)}
            {step === 3 && (
                <>
                    <Grid2 container className={listClasses.container} direction="column" sx={{ marginTop: "1rem", marginLeft: "3rem" }}>
                        {matingsRelationships.length > 0 && (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                                    <Typography variant="h6" className={listClasses.form_title}>
                                        {`Ο κριός "${selectedMaleAnimal}" δεν επιτρέπεται να συζευχθεί με τις εξής προβατίνες της εκτροφής "${selectedFemaleOwner}"`}
                                    </Typography>
                                    <Box>
                                        <Button variant="contained" sx={{ marginRight: "0.5rem", backgroundColor: "secondary.main" }} onClick={() => setStep(4)}>
                                            PDF
                                        </Button>
                                    </Box>
                                </Box>
                                <Table
                                    data={matingsRelationships}
                                    noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν εγγραφές!"}
                                    columns={tableColumns}
                                />
                            </>
                        )}
                        {matingsRelationships.length === 0 && (
                            <>
                                <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ width: "90%", marginBottom: "1rem" }}>
                                    <Typography variant="h6" className={listClasses.form_title} sx={{ color: "black" }}>
                                        {`Δεν υπάρχουν ζώα που να έχουν απαγορευτική συγγένεια με τον κριό "${selectedMaleAnimal}" στην εκτροφή "${selectedFemaleOwner}"`}
                                    </Typography>
                                </Box>
                            </>
                        )}
                    </Grid2>
                </>
            )}
            {step === 4 && (
                <MatingsRelationshipsPdf
                    matingsRelationships={matingsRelationships}
                    femaleOwner={selectedFemaleOwner}
                    maleOwner={selectedNameMaleOwner}
                    maleAnimal={selectedMaleAnimal}
                />
            )}
        </>
    )
}

export default memo(MatingsRelationship);